/**
 * My Packets — every finished packet for a site, newest first.
 *
 * ── The ordering finding this unit exists to fix ──────────────────────────────────────────────
 * The API already returns rows `generated_at desc`. The old page rendered that order but displayed
 * only the COVERED PERIOD, so the visible column was not the sort key and the list read as
 * unordered. Measured on Foster Rd dev, in returned order, the period column ran:
 *   08-06, 08-05, 08-04, 08-03, 07-21, 07-28, 08-04, 08-11, ...
 * Nothing was wrong with the sort; the user simply could not see what it was sorted by. So the
 * creation date is now shown as the primary chronological anchor and the period is presented as
 * the packet's identity — the two are different questions ("which one did I make last?" vs "what
 * does this one cover?") and conflating them is what made the list look shuffled.
 *
 * The sort is ALSO applied explicitly here rather than inherited from the server. The page must not
 * depend on an ordering promise made in a repository three layers away that no frontend test covers.
 *
 * ── What is hidden, and why that is safe ──────────────────────────────────────────────────────
 * `MANUAL_ASSEMBLY` and `evidence_class: null` are hidden ENTIRELY — no toggle, no advanced mode.
 * They are not evidence: is_evidentiary_class() is False for both, and showing them next to real
 * packets is what made the old list untrustworthy. On dev this hides 17 of Foster Rd's 26 rows.
 * They remain reachable for QA/engineering through the preserved legacy tool at /ce/bundle-export
 * (load-by-ID and manual assembly), through Evidence Explorer search, and through the
 * ce_get_bundle_manifest LLM tool. No access is lost — it is removed from the customer surface only.
 *
 * ── The two seal signals, which disagree on real data ─────────────────────────────────────────
 * Verified on dev 2026-08-14: bundle 3dc486f6 carries `evidence_class: null` but a PRESENT
 * `sealed_bundle_integrity_hash`. So a seal hash does NOT imply an evidentiary class — pre-D1
 * bundles were sealed before the class existed and the b7e2d9a41c65 backfill deliberately left them
 * NULL. Gating on the seal hash alone would therefore offer downloads for a record the system does
 * not consider evidence. The class allow-list is the gate; the seal hash is a cross-check that can
 * only ever take downloads away, never grant them. Both must agree before anything is offered, and
 * an unverified seal counts as disagreement — see the note at the download panel below.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { errorDetail } from '../services/api';
import { PacketDownloads } from './CEPacketDownloads';

/** The project-wide rule, stated as an allow-list so an unknown class can never pass. */
export const isEvidentiary = (c) => c === 'SEALED_EVIDENCE' || c === 'PARTIAL_REVIEW';

/**
 * Plain language, no internal enum ever shown. `classification` is the submittability judgement and
 * is NOT in the list payload, so it costs one manifest lookup per visible row (measured ~0.3s each,
 * fanned out in parallel). A lookup that fails renders "Status unavailable" — never a guessed
 * status, and never the optimistic one.
 */
export function packetStatus(classification) {
  switch (classification) {
    case 'EXTERNAL_REVIEW_READY':
      return { label: 'Complete — ready for external review', tone: 'good' };
    case 'SCOPED_REVIEW_PARTIAL':
      return { label: 'Bounded review set — not a complete packet', tone: 'warn' };
    case 'INTERNAL_RESEARCH_ONLY':
      return { label: 'Internal research only — not for submission', tone: 'warn' };
    case 'COMPLETENESS_UNDETERMINED':
      // Its own state, deliberately worded as an absence of evidence rather than as a
      // deficiency. "Bounded review set" would claim we know what is missing; we do not.
      // Every packet whose period predates derivation recording availability reads this way.
      return { label: 'Completeness could not be determined', tone: 'warn' };
    case 'LEGACY_INCOMPLETE_METADATA':
      return { label: 'Older packet — provenance incomplete', tone: 'warn' };
    case 'GENERATION_FAILED':
      return { label: 'Generation failed', tone: 'bad' };
    default:
      return { label: 'Status unavailable', tone: 'unknown' };
  }
}

const TONE = {
  good: 'bg-green-900/40 text-green-300 border-green-800',
  warn: 'bg-amber-900/40 text-amber-300 border-amber-800',
  bad: 'bg-red-900/40 text-red-300 border-red-800',
  unknown: 'bg-gray-700/40 text-gray-400 border-gray-600',
};

const PAGE_SIZE = 25;

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function fmtDateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function CEMyPackets() {
  const [searchParams] = useSearchParams();
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(searchParams.get('site') || '');
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState({});      // bundleId -> {classification, sealed} | 'error'
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [shown, setShown] = useState(PAGE_SIZE);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get('/ce/sites').then((r) => setSites(r.data.sites || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!siteId) { setRows([]); return; }
    setLoading(true);
    setErr(null);
    setOpen(null);
    setShown(PAGE_SIZE);
    try {
      const { data } = await api.get(`/ce/sites/${siteId}/bundles`);
      const all = data.bundles || [];
      // Hide first, then sort. Sorting is explicit — see the header note.
      const visible = all
        .filter((b) => isEvidentiary(b.evidence_class))
        .sort((a, b) => new Date(b.generated_at || 0) - new Date(a.generated_at || 0));
      setRows(visible);
    } catch (e) {
      setErr(errorDetail(e, 'Could not load your packets.'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const page = useMemo(() => rows.slice(0, shown), [rows, shown]);

  // Status detail for the rendered rows only. allSettled: one failed lookup must not blank the page,
  // and it must not be silently treated as a status either — it becomes "Status unavailable".
  useEffect(() => {
    let cancelled = false;
    const need = page.filter((b) => detail[b.id] === undefined);
    if (!need.length) return undefined;
    (async () => {
      const results = await Promise.allSettled(
        need.map((b) => api.get(`/ce/bundles/${b.id}/manifest`, { timeout: 15000 })),
      );
      if (cancelled) return;
      const next = {};
      need.forEach((b, i) => {
        const r = results[i];
        next[b.id] = r.status === 'fulfilled'
          ? {
            classification: r.value.data.classification,
            // Cross-check only. It can withhold downloads, never grant them.
            sealed: Boolean(r.value.data.sealed_bundle_integrity_hash),
          }
          : 'error';
      });
      setDetail((d) => ({ ...d, ...next }));
    })();
    return () => { cancelled = true; };
  }, [page, detail]);

  const siteName = sites.find((s) => s.id === siteId)?.site_name || '';

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">My Packets</h1>
          <p className="text-sm text-gray-400 mt-1">
            Every compliance packet you have created, newest first.
          </p>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5">
          <label className="block text-xs text-gray-400 mb-1">Site</label>
          <select
            data-testid="site-select"
            value={siteId}
            onChange={(e) => { setSiteId(e.target.value); setDetail({}); }}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Select site</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.site_name}</option>)}
          </select>
        </div>

        {loading && <p className="text-xs text-gray-500">Loading your packets…</p>}

        {err && (
          <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300">
            {err}
          </div>
        )}

        {!loading && !err && siteId && rows.length === 0 && (
          <div data-testid="packets-empty" className="bg-gray-800 rounded-xl border border-gray-700 p-8 text-center">
            <div className="text-sm font-semibold text-white">No packets yet</div>
            <p className="text-xs text-gray-400 mt-2">
              Nothing has been created for {siteName} yet.{' '}
              <a href={`/ce/evidence-packet?site=${siteId}`} className="text-blue-400 hover:text-blue-300 underline">
                Generate a packet
              </a>.
            </p>
          </div>
        )}

        {page.length > 0 && (
          <div className="space-y-3">
            {page.map((b) => {
              const d = detail[b.id];
              const known = d && d !== 'error';
              const status = packetStatus(known ? d.classification : undefined);
              const isOpen = open === b.id;
              return (
                <div
                  key={b.id}
                  data-testid={`packet-${b.id}`}
                  className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : b.id)}
                    className="w-full text-left p-5 hover:bg-gray-750 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        {/* Identity: what this packet covers. */}
                        <div className="text-sm font-semibold text-white">
                          {fmtDate(b.period_start)} – {fmtDate(b.period_end)}
                        </div>
                        {/* The sort key, made visible. This is the fix for the ordering finding. */}
                        <div data-testid="packet-created" className="text-xs text-gray-500 mt-1">
                          Created {fmtDateTime(b.generated_at)}
                        </div>
                      </div>
                      <span
                        data-testid={`packet-status-${b.id}`}
                        className={`shrink-0 px-2 py-0.5 text-[11px] rounded border ${TONE[status.tone]}`}
                      >
                        {status.label}
                      </span>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-5">
                      {/* Both signals must agree, and an UNVERIFIED seal is never treated as sealed.
                          Defaulting the unknown case to `true` was the first implementation here: it
                          offered downloads during the loading window before anything had been
                          checked, which is the silent-affirmation failure in the one place this
                          redesign exists to prevent it. Nothing is offered until the seal is
                          positively confirmed, and a failed check says so in its own words rather
                          than borrowing the "not sealed yet" copy, which would be a false claim. */}
                      {d === undefined && (
                        <p data-testid="packet-seal-checking" className="text-xs text-gray-500">
                          Checking this packet…
                        </p>
                      )}
                      {d === 'error' && (
                        <div
                          data-testid="packet-seal-unknown"
                          className="bg-gray-800 rounded-xl border border-gray-700 p-5 text-sm text-gray-400"
                        >
                          This packet&apos;s seal could not be confirmed just now, so its files are not
                          offered. Reopen it in a moment to try again.
                        </div>
                      )}
                      {known && (
                        <PacketDownloads
                          bundleId={b.id}
                          evidenceClass={b.evidence_class}
                          sealed={d.sealed}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {rows.length > page.length && (
          <button
            type="button"
            data-testid="show-more"
            onClick={() => setShown((n) => n + PAGE_SIZE)}
            className="w-full px-4 py-2 text-sm rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            {/* Never a silent cap — the count that is not shown is stated. */}
            Show older packets ({rows.length - page.length} more)
          </button>
        )}
      </div>
    </div>
  );
}
